import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { authHeaders, Toast } from './listPageStyles';
import { StatusBadge, RelationPicker } from './ui';
import { RELATION_CONFIGS } from './relationConfigs';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  DetailShell, Section, Grid, Icon, SubModal, SplitButton,
  FieldText, FieldSelect, FieldDate, FieldCustom,
} from './detail/DetailShell';
import { usePageActions } from '../contexts/PermissionContext';

// /api/coolers REST (OData değil) — GET-by-id YOK. Tek kayıt önce navigation state'ten
// gelen row ile, yoksa listeyi (take=500) çekip id ile bulunarak yüklenir.
// CRUD eski REST sözleşmesi (POST / PUT/{id} / DELETE/{id}) birebir korunur.
const API_BASE = '/api/coolers';

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Aktif' },
  { value: 'FAULTY', label: 'Arızalı' },
  { value: 'INACTIVE', label: 'Pasif' },
  { value: 'REMOVED', label: 'Sökülmüş' },
];
const STATUS_META = {
  ACTIVE: { label: 'Aktif', tone: 'ok' },
  FAULTY: { label: 'Arızalı', tone: 'bad' },
  INACTIVE: { label: 'Pasif', tone: 'neutral' },
  REMOVED: { label: 'Sökülmüş', tone: 'neutral' },
};

const EMPTY_FORM = {
  textId: '',
  customerId: '',
  customerTitle: '',
  barcode: '',
  brand: '',
  model: '',
  doorCount: 1,
  status: 'ACTIVE',
  installedAt: '',
  lastServiceAt: '',
  temperatureCelsius: '',
  photoUrl: '',
};

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
}
function unwrap(payload) {
  const data = pick(payload, 'Data', 'data') ?? payload;
  return data;
}
function toLocalDateInput(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  } catch { return ''; }
}
function fromLocalDateInput(value) {
  if (!value) return null;
  try { return new Date(`${value}T00:00:00`).toISOString(); } catch { return null; }
}
function customerLookupLabel(item) {
  if (!item) return '';
  const code = item.textId || item.TextId || '';
  const title = item.title || item.Title || item.name || item.Name || '';
  return title ? (code ? `${code} - ${title}` : title) : code;
}

function rowToForm(row) {
  return {
    textId: pick(row, 'textId', 'TextId') || '',
    customerId: pick(row, 'customerId', 'CustomerId') || '',
    customerTitle: pick(row, 'customerTitle', 'CustomerTitle') || '',
    barcode: pick(row, 'barcode', 'Barcode') || '',
    brand: pick(row, 'brand', 'Brand') || '',
    model: pick(row, 'model', 'Model') || '',
    doorCount: pick(row, 'doorCount', 'DoorCount') ?? 1,
    status: pick(row, 'status', 'Status') || 'ACTIVE',
    installedAt: toLocalDateInput(pick(row, 'installedAt', 'InstalledAt')),
    lastServiceAt: toLocalDateInput(pick(row, 'lastServiceAt', 'LastServiceAt')),
    temperatureCelsius: pick(row, 'temperatureCelsius', 'TemperatureCelsius') ?? '',
    photoUrl: pick(row, 'photoUrl', 'PhotoUrl') || '',
  };
}

export function CoolerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  const { canAdd, canEdit, canDelete, canCopy } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;
  usePageMeta(isNew ? 'Yeni Cooler' : 'Cooler Detayı', 'Saha noktalarındaki buzdolabı / soğutucu master kaydı.');

  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [audit, setAudit] = useState(null);

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };
  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));

  // GET-by-id yok: önce navigation state'ten gelen row, yoksa listeyi çekip id ile bul.
  const load = useCallback(async () => {
    if (isNew) { setLoading(false); return; }
    const stateRow = location.state?.row;
    if (stateRow && String(pick(stateRow, 'id', 'Id')) === String(id)) {
      setForm(rowToForm(stateRow));
      // Nav-state satırı (liste $select) audit alanlarını içermez → tam kaydı arka planda
      // (bloklamadan) listeden bulup audit'i doldur (GET-by-id yok).
      (async () => {
        try {
          const params = new URLSearchParams();
          params.set('take', '500');
          const res = await fetch(`${API_BASE}?${params.toString()}`, { headers: authHeaders() });
          if (!res.ok) return;
          const data = unwrap(await res.json());
          const items = pick(data, 'items', 'Items') ?? (Array.isArray(data) ? data : []);
          const rec = (Array.isArray(items) ? items : []).find((r) => String(pick(r, 'id', 'Id')) === String(id));
          if (rec) setAudit({ createdBy: pick(rec, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(rec, 'createdDate', 'CreatedDate'), updatedBy: pick(rec, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(rec, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
        } catch { /* yoksay */ }
      })();
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('take', '500');
      const res = await fetch(`${API_BASE}?${params.toString()}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Cooler alınamadı (${res.status})`);
      const data = unwrap(await res.json());
      const items = pick(data, 'items', 'Items') ?? (Array.isArray(data) ? data : []);
      const row = (Array.isArray(items) ? items : []).find((r) => String(pick(r, 'id', 'Id')) === String(id));
      if (!row) throw new Error('Kayıt bulunamadı');
      setForm(rowToForm(row));
      setAudit({ createdBy: pick(row, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(row, 'createdDate', 'CreatedDate'), updatedBy: pick(row, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(row, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
    } catch (err) {
      showToast('error', err.message || 'Cooler alınamadı');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  useEffect(() => { load(); }, [load]);

  // Klonla: /coolers/new'e kopya veriyle gelindiyse formu doldur (kayıt OLUŞTURMA — kullanıcı
  // Kaydet diyene kadar). location.key her navigasyonda değiştiği için klon bir kez uygulanır.
  useEffect(() => {
    if (isNew && location.state?.clone) {
      setForm(location.state.clone);
      setTried(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const buildBody = () => ({
    textId: (form.textId || '').trim() || null,
    customerId: (form.customerId || '').trim(),
    barcode: (form.barcode || '').trim(),
    brand: (form.brand || '').trim() || null,
    model: (form.model || '').trim() || null,
    doorCount: Number(form.doorCount) || 0,
    status: form.status,
    installedAt: fromLocalDateInput(form.installedAt),
    lastServiceAt: fromLocalDateInput(form.lastServiceAt),
    temperatureCelsius: form.temperatureCelsius === '' ? null : Number(form.temperatureCelsius),
    photoUrl: (form.photoUrl || '').trim() || null,
  });

  // andNew: kaydet ama detaya/listeye gitme — formu sıfırlayıp yeni boş kayda hazırlan
  // (önceki cooler backend'e kaydedilmiş olur). Sadece yeni-kayıt modunda anlamlı.
  const persist = async ({ andNew = false } = {}) => {
    setTried(true);
    if (!String(form.customerId || '').trim()) { showToast('error', 'Müşteri zorunlu.'); return; }
    if (!String(form.barcode || '').trim()) { showToast('error', 'Barkod zorunlu.'); return; }
    setSaving(true);
    try {
      const url = isNew ? API_BASE : `${API_BASE}/${id}`;
      const res = await fetch(url, { method: isNew ? 'POST' : 'PUT', headers: authHeaders(), body: JSON.stringify(buildBody()) });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || `İşlem başarısız (${res.status})`);
      if (isNew) {
        if (andNew) {
          showToast('success', 'Cooler eklendi. Yeni kayda devam edebilirsiniz.');
          setForm(EMPTY_FORM);
          setTried(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        showToast('success', 'Cooler oluşturuldu.');
        setTimeout(() => navigate('/coolers'), 400);
        return;
      }
      showToast('success', 'Cooler güncellendi.');
      setTimeout(() => navigate('/coolers'), 400);
    } catch (err) {
      showToast('error', err.message || 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => persist();

  // Klonla: mevcut kaydı kopyalayıp yeni-kayıt ekranını aç. Hiçbir şey kaydedilmez;
  // kullanıcı "Kaydet" diyene kadar yeni cooler oluşmaz.
  const handleClone = () => {
    navigate('/coolers/new', { state: { clone: { ...form } } });
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/${id}`, { method: 'DELETE', headers: authHeaders() });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || `Silme başarısız (${res.status})`);
      showToast('success', 'Cooler silindi.');
      setTimeout(() => navigate('/coolers'), 400);
    } catch (err) {
      showToast('error', err.message || 'Silme başarısız');
      setSaving(false);
      setDeleteConfirm(false);
    }
  };

  const title = isNew ? 'Yeni Cooler' : (form.barcode || 'Cooler Detayı');
  const stMeta = STATUS_META[form.status] || { label: form.status || '-', tone: 'neutral' };
  const statusBadge = !isNew && <StatusBadge tone={stMeta.tone}>{stMeta.label}</StatusBadge>;

  const actions = (
    <>
      {!isNew && canDelete && <button className="dps-btn danger" onClick={() => setDeleteConfirm(true)} disabled={saving}><Icon name="x" size={16} /> Sil</button>}
      <button className="dps-btn ghost" onClick={() => navigate('/coolers')} disabled={saving}>İptal</button>
      {canSave && (isNew ? (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Kaydet ve Yeni',
            desc: 'Kaydet, listeye dönme, yeni kayıt aç',
            icon: 'plus',
            onClick: () => persist({ andNew: true }),
          }]}
        />
      ) : (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Klonla',
            desc: 'Bu kaydı kopyalayıp yeni cooler aç',
            icon: 'copy',
            onClick: handleClone,
          }]}
        />
      ))}
    </>
  );

  return (
    <DetailShell
      crumb={[{ label: 'Soğutucular', to: '/coolers' }, { label: isNew ? 'Yeni Cooler' : 'Cooler Detayı', current: true }]}
      status={statusBadge}
      audit={isNew ? null : audit}
      history={isNew ? null : { entityName: 'Cooler', entityId: id }}
      actions={actions}
      loading={loading}
    >
      <div className="dps-form">
        <Section icon="box" title="Cooler Bilgileri" subtitle="Nokta, barkod ve teknik tanım">
          <Grid cols={2}>
            <FieldCustom label="Müşteri" req invalid={tried && !String(form.customerId || '').trim()}>
              <RelationPicker
                config={RELATION_CONFIGS.customer}
                value={form.customerId || ''}
                selectedLabel={form.customerTitle || ''}
                onChange={(val, item) => setForm((c) => ({ ...c, customerId: val || '', customerTitle: item ? customerLookupLabel(item) : '' }))}
                placeholder="Müşteri kodu veya adı ile ara…"
              />
            </FieldCustom>
            <FieldSelect form={form} set={set} k="status" label="Durum" options={STATUS_OPTIONS} req />
            <FieldText form={form} set={set} tried={tried} k="barcode" label="Barkod" req mono placeholder="örn. CLR-2026-00184" />
            <FieldText form={form} set={set} k="textId" label="TextId" />
            <FieldText form={form} set={set} k="brand" label="Marka" />
            <FieldText form={form} set={set} k="model" label="Model" />
            <FieldText form={form} set={set} k="doorCount" label="Kapı Sayısı" num />
          </Grid>
        </Section>

        <Section icon="calendar" title="Servis & Ölçüm" subtitle="Kurulum, servis tarihleri ve sıcaklık">
          <Grid cols={3}>
            <FieldDate form={form} set={set} k="installedAt" label="Kurulum Tarihi" />
            <FieldDate form={form} set={set} k="lastServiceAt" label="Son Servis Tarihi" />
            <FieldText form={form} set={set} k="temperatureCelsius" label="Sıcaklık (°C)" num placeholder="(opsiyonel)" />
          </Grid>
        </Section>

        <Section icon="info" title="Fotoğraf" subtitle="Cooler görseli (URL)">
          <Grid cols={2}>
            <FieldText form={form} set={set} k="photoUrl" label="Fotoğraf URL" placeholder="https://..." span />
          </Grid>
          {form.photoUrl && (
            <img
              src={form.photoUrl}
              alt="cooler"
              style={{ marginTop: 8, maxWidth: 200, maxHeight: 150, borderRadius: 8, border: '1px solid var(--border-color, #e5e7eb)' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          )}
        </Section>
      </div>

      {/* Silme onayı */}
      {deleteConfirm && (
        <SubModal
          title="Cooler Sil"
          maxWidth={440}
          onClose={() => setDeleteConfirm(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setDeleteConfirm(false)} disabled={saving}>İptal</button>
              <button className="dps-btn danger" onClick={handleDelete} disabled={saving}>{saving ? 'Siliniyor…' : 'Evet, Sil'}</button>
            </>
          )}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            <strong>“{form.barcode || title}”</strong> coolerını silmek istediğinize emin misiniz?
          </p>
          <p style={{ margin: '8px 0 0', fontSize: 12, color: '#E53E3E' }}>
            Bağlı arıza bildirimleri (CoolerFaultReport) etkilenmez (string barkod ile bağlı).
          </p>
        </SubModal>
      )}

      <Toast toast={toast} />
    </DetailShell>
  );
}

export default CoolerDetailPage;
